(() => {
  const layout = document.querySelector('[data-article-layout]');
  if (!layout) return;

  const sidebar = layout.querySelector('[data-article-sidebar]');
  const tocNav = layout.querySelector('[data-article-toc-nav]');
  const tocList = layout.querySelector('[data-article-toc]');
  const tocToggle = layout.querySelector('[data-article-toc-toggle]');
  const tocPanel = layout.querySelector('[data-article-toc-panel]');
  const content = layout.querySelector('[data-article-content]');
  if (!sidebar || !tocNav || !tocList || !content) return;

  const introductionLabel = sidebar.dataset.introductionLabel || 'Introduction';
  const desktopMq = window.matchMedia('(min-width: 992px)');

  const slugify = (value) =>
    value
      .toLowerCase()
      .trim()
      .replace(/['’]/g, '')
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-+|-+$/g, '');

  const normalizeText = (value) => value.replace(/\s+/g, ' ').trim().toLowerCase();

  const ensureUniqueId = (baseId, usedIds) => {
    let id = baseId || 'section';
    let index = 2;
    while (usedIds.has(id) || document.getElementById(id)) {
      id = `${baseId}-${index}`;
      index += 1;
    }
    usedIds.add(id);
    return id;
  };

  const createTocItem = (id, label) => {
    const item = document.createElement('li');
    item.className = 'article-toc__item position-relative d-block m-0 p-0';
    item.setAttribute('role', 'option');
    item.dataset.value = id;

    const link = document.createElement('a');
    link.className = 'article-toc__link option_txt position-relative d-block py-2 pe-4 ps-7 text-black fs-md fw-normal text-decoration-none';
    link.href = `#${id}`;
    link.textContent = label;

    item.appendChild(link);
    return { item, link };
  };

  const headings = [...content.querySelectorAll('h2')].filter((heading) => {
    if (heading.closest('.article-featured-products')) return false;
    return heading.textContent.trim().length > 0;
  });

  const existingItems = [...tocList.querySelectorAll('.article-toc__item')];
  const usedIds = new Set();
  const sections = [];

  if (existingItems.length) {
    content.id = content.id || 'introduction';
    content.tabIndex = -1;
    usedIds.add(content.id);

    const claimedHeadings = new Set();
    let headingIndex = 0;

    const claimHeading = (candidate) => {
      if (!candidate || claimedHeadings.has(candidate)) return null;
      claimedHeadings.add(candidate);
      return candidate;
    };

    existingItems.forEach((item) => {
      const link = item.querySelector('a[href^="#"]');
      if (!link) return;

      const id = item.dataset.value || link.getAttribute('href').slice(1);
      if (!id) return;

      usedIds.add(id);
      link.href = `#${id}`;

      let heading = null;
      if (id !== content.id) {
        const label = normalizeText(link.textContent);
        heading = claimHeading(headings.find((node) => node.id === id))
          || claimHeading(headings.find((node) => normalizeText(node.textContent) === label))
          || claimHeading(headings.find((node) => slugify(node.textContent.trim()) === id))
          || claimHeading(headings.slice(headingIndex).find((node) => !claimedHeadings.has(node)));

        if (heading) {
          heading.id = id;
          heading.tabIndex = -1;
          const matchedIndex = headings.indexOf(heading);
          headingIndex = Math.max(headingIndex, matchedIndex + 1);
        }
      }

      sections.push({
        id,
        link,
        item,
        label: link.textContent.trim(),
        heading,
      });
    });
  } else {
    if (!headings.length) {
      sidebar.hidden = true;
      return;
    }

    const introId = ensureUniqueId('introduction', usedIds);
    content.id = content.id || introId;
    content.tabIndex = -1;

    const intro = createTocItem(content.id, introductionLabel);
    tocList.appendChild(intro.item);
    sections.push({ id: content.id, link: intro.link, item: intro.item, label: introductionLabel });

    headings.forEach((heading) => {
      const label = heading.textContent.trim();
      const id = heading.id || ensureUniqueId(slugify(label), usedIds);
      usedIds.add(id);
      heading.id = id;
      heading.tabIndex = -1;

      const tocItem = createTocItem(id, label);
      tocList.appendChild(tocItem.item);
      sections.push({ id, link: tocItem.link, item: tocItem.item, label, heading });
    });
  }

  if (!sections.length) {
    sidebar.hidden = true;
    return;
  }

  sidebar.hidden = false;

  const isDesktop = () => desktopMq.matches;

  const openDropdown = () => {
    if (!tocToggle || !tocPanel || isDesktop()) return;
    tocNav.classList.add('open');
    tocPanel.classList.add('open');
    tocToggle.setAttribute('aria-expanded', 'true');
    tocPanel.setAttribute('aria-hidden', 'false');
    tocPanel.removeAttribute('inert');
  };

  const closeDropdown = () => {
    if (!tocToggle || !tocPanel) return;
    tocNav.classList.remove('open');
    tocPanel.classList.remove('open');
    tocToggle.setAttribute('aria-expanded', 'false');
    tocPanel.setAttribute('aria-hidden', 'true');
    if (!isDesktop()) {
      tocPanel.setAttribute('inert', '');
    }
  };

  const syncDesktopPanel = () => {
    if (!tocPanel || !tocToggle) return;

    if (isDesktop()) {
      tocNav.classList.remove('open');
      tocPanel.classList.add('open');
      tocToggle.setAttribute('aria-expanded', 'true');
      tocPanel.setAttribute('aria-hidden', 'false');
      tocPanel.removeAttribute('inert');
      return;
    }

    closeDropdown();
  };

  const setActive = (activeId) => {
    sections.forEach(({ id, link, item }) => {
      const isActive = id === activeId;
      link.classList.toggle('is-active', isActive);
      link.classList.toggle('bg-light-gray', isActive);
      // link.classList.toggle('fw-semibold', isActive);
      link.classList.toggle('fw-normal', !isActive);
      item.classList.toggle('is-active', isActive);
      item.classList.toggle('selected', isActive);

      if (isActive) {
        link.setAttribute('aria-current', 'true');
        item.setAttribute('aria-selected', 'true');
      } else {
        link.removeAttribute('aria-current');
        item.setAttribute('aria-selected', 'false');
      }
    });
  };

  const getStickyGap = () => (isDesktop() ? 30 : 8);

  const getHeaderElement = () => document.querySelector('.section-header');

  const isStickyHeaderVisible = () => {
    const header = getHeaderElement();
    if (!header) return false;
    return header.classList.contains('header-sticky') && !header.classList.contains('header-hidden');
  };

  const syncStickyOffset = () => {
    const header = getHeaderElement();
    const gap = getStickyGap();
    const headerHeight = isStickyHeaderVisible()
      ? Math.round(header?.offsetHeight || 0)
      : 0;
    const stickyTop = headerHeight + gap;

    sidebar.style.setProperty('--article-sidebar-sticky-top', `${stickyTop}px`);
    sidebar.style.setProperty(
      '--article-sidebar-sticky-max-height',
      `calc(100svh - ${stickyTop + gap}px)`
    );
    document.documentElement.style.setProperty(
      '--article-toc-sticky-offset',
      `${!isDesktop() ? Math.round(tocNav.getBoundingClientRect().height) || 70 : 0}px`
    );
  };

  // Height that the sticky bars cover. The full header height is reserved
  // instead of its current on-screen height, because the header reveals itself
  // again on any upward scroll and would otherwise hide the target heading.
  const getOverlayHeight = () => {
    const header = getHeaderElement();
    const headerHeight = header ? Math.round(header.offsetHeight || 0) : 0;
    if (isDesktop()) return headerHeight;
    return headerHeight + Math.round(tocNav.getBoundingClientRect().height) + 8;
  };

  const getHeaderOffset = () => getOverlayHeight() + getStickyGap();

  const getSectionTarget = (section) => {
    if (!section) return null;
    if (section.id === content.id) return content;
    return section.heading || content.querySelector(`#${CSS.escape(section.id)}`);
  };

  // Land the heading right below the sticky bars, using the empty space its own
  // top margin already provides. A larger gap would leave the tail of the
  // previous paragraph peeking out from under the header.
  const getTargetClearance = (target) => {
    const gap = getStickyGap();
    const previous = target === content ? null : target.previousElementSibling;
    if (!previous) return gap;

    const whitespace = Math.round(
      target.getBoundingClientRect().top - previous.getBoundingClientRect().bottom
    );
    return Math.max(0, Math.min(gap, whitespace));
  };

  let lockedId = null;
  let settleFrame = null;

  const cancelSettle = () => {
    if (settleFrame) cancelAnimationFrame(settleFrame);
    settleFrame = null;
  };

  const onScrollSettled = (callback) => {
    cancelSettle();

    let lastY = window.scrollY;
    let stableFrames = 0;
    const startedAt = performance.now();

    const tick = () => {
      const y = window.scrollY;
      stableFrames = Math.abs(y - lastY) <= 1 ? stableFrames + 1 : 0;
      lastY = y;

      if (stableFrames >= 4 || performance.now() - startedAt > 1500) {
        settleFrame = null;
        callback();
        return;
      }

      settleFrame = requestAnimationFrame(tick);
    };

    settleFrame = requestAnimationFrame(tick);
  };

  let userInteracted = false;

  const releaseLock = () => {
    cancelSettle();
    lockedId = null;
  };

  const onUserScrollIntent = () => {
    userInteracted = true;
    releaseLock();
  };

  const updateActiveFromScroll = () => {
    if (lockedId) return;

    const offset = getHeaderOffset() + 8;
    const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
    let currentId = sections[0].id;

    // Near the top of the page, always keep Introduction active.
    if (scrollTop <= 4) {
      setActive(currentId);
      return;
    }

    // At the end of the page the last section can be too short to cross the
    // offset line, so activate it explicitly.
    const atBottom = scrollTop + window.innerHeight >= document.documentElement.scrollHeight - 2;
    if (atBottom) {
      const last = [...sections].reverse().find((section) => {
        const target = getSectionTarget(section);
        return target && target.getBoundingClientRect().top < window.innerHeight;
      });
      if (last) {
        setActive(last.id);
        return;
      }
    }

    sections.forEach((section) => {
      const target = getSectionTarget(section);
      if (!target) return;

      if (target.getBoundingClientRect().top - offset <= 0) {
        currentId = section.id;
      }
    });

    setActive(currentId);
  };

  const scrollToSection = (id, { focusTarget = false, behavior = 'smooth' } = {}) => {
    const section = sections.find((entry) => entry.id === id);
    const target = getSectionTarget(section);
    if (!target) return false;

    lockedId = id;
    setActive(id);

    const getRestingTop = () => getOverlayHeight() + getTargetClearance(target);

    const jump = (scrollBehavior) => {
      const maxTop = Math.max(0, document.documentElement.scrollHeight - window.innerHeight);
      const top = Math.min(
        maxTop,
        Math.max(0, Math.round(window.scrollY + target.getBoundingClientRect().top - getRestingTop()))
      );
      window.scrollTo({ top, behavior: scrollBehavior });
    };

    const finish = () => {
      if (focusTarget) target.focus({ preventScroll: true });
      releaseLock();
      updateActiveFromScroll();
    };

    jump(behavior);

    // Lazy media and fonts can shift the layout while the smooth scroll runs,
    // so re-measure once it settles and correct the final position.
    onScrollSettled(() => {
      if (lockedId !== id) return;

      const drift = Math.round(target.getBoundingClientRect().top - getRestingTop());
      if (Math.abs(drift) > 2) {
        jump('auto');
        onScrollSettled(() => {
          if (lockedId !== id) return;
          finish();
        });
        return;
      }

      finish();
    });

    return true;
  };

  tocList.addEventListener('click', (event) => {
    const link = event.target.closest('a[href^="#"]');
    if (!link) return;

    const targetId = link.getAttribute('href').slice(1);
    if (!sections.some((section) => section.id === targetId)) return;

    event.preventDefault();
    if (!isDesktop()) closeDropdown();
    if (!scrollToSection(targetId, { focusTarget: true })) return;
    history.replaceState(null, '', `#${targetId}`);
  });

  // A real scroll intent always wins over an in-flight programmatic scroll.
  ['wheel', 'touchstart', 'keydown'].forEach((eventName) => {
    window.addEventListener(eventName, onUserScrollIntent, { passive: true });
  });

  tocToggle?.addEventListener('click', (event) => {
    event.preventDefault();
    if (isDesktop()) return;
    tocNav.classList.contains('open') ? closeDropdown() : openDropdown();
  });

  document.addEventListener('click', (event) => {
    if (isDesktop() || !tocNav.classList.contains('open')) return;
    if (event.target.closest('[data-article-toc-nav]')) return;
    closeDropdown();
  });

  document.addEventListener('keyup', (event) => {
    if (event.key === 'Escape' && tocNav.classList.contains('open')) {
      closeDropdown();
      tocToggle?.focus();
    }
  });

  // Frame-throttled instead of debounced so the highlight follows the scroll
  // position while scrolling, not only once it stops.
  let scrollFrame = null;
  const onScroll = () => {
    if (scrollFrame) return;
    scrollFrame = requestAnimationFrame(() => {
      scrollFrame = null;
      updateActiveFromScroll();
    });
  };

  const header = getHeaderElement();
  if (header && typeof MutationObserver !== 'undefined') {
    new MutationObserver(() => {
      requestAnimationFrame(syncStickyOffset);
    }).observe(header, { attributes: true, attributeFilter: ['class'] });
  }

  window.addEventListener('scroll', onScroll, { passive: true });
  window.addEventListener('resize', () => {
    syncStickyOffset();
    onScroll();
  });
  desktopMq.addEventListener('change', () => {
    syncDesktopPanel();
    syncStickyOffset();
  });

  syncDesktopPanel();
  syncStickyOffset();
  setActive(sections[0].id);
  updateActiveFromScroll();

  window.addEventListener('load', updateActiveFromScroll, { once: true });
  requestAnimationFrame(updateActiveFromScroll);

  if (window.location.hash) {
    const hashId = window.location.hash.slice(1);
    if (sections.some((section) => section.id === hashId)) {
      setActive(hashId);
      window.addEventListener(
        'load',
        () => {
          if (!userInteracted) scrollToSection(hashId, { behavior: 'auto' });
        },
        { once: true }
      );
    }
  }
})();
